import { useCallback, useMemo } from "react";
import useSWR, { SWRResponse } from "swr";

import {
    UsePaginationServiceResult,
    usePaginationService,
} from "./PaginationService";
import { useFetch } from "../hooks/useFetch";
import { Error } from "../models/error/Error";
import { Pagination } from "../models/pagination/Pagination";
import {
    WorkOrder,
    WorkOrderEdit,
    WorkOrderOverview,
} from "../models/workorder/WorkOrder";

export type EditWorkOrderListCallback = (
    id: number,
    model: WorkOrderEdit,
) => Promise<void>;
export type DeleteWorkOrderListCallback = (id: number) => Promise<void>;

interface WorkOrderServiceListResult {
    workOrdersResponse: UsePaginationServiceResult<WorkOrderOverview, Error>;
    editWorkOrder: EditWorkOrderListCallback;
    deleteWorkOrder: DeleteWorkOrderListCallback;
}

interface WorkOrderServiceViewResult {
    workOrderResponse: SWRResponse<WorkOrder | undefined, Error>;
}

const WorkOrderListPath = "workorders";
const WorkOrderGetPath = (id: number) => `workorders/${id}`;
const WorkOrderEditPath = (id: number) => `workorders/${id}`;
const WorkOrderDeletePath = (id: number) => `workorders/${id}`;

export function useWorkOrderListService(
    defaultPagination: Pagination,
    customerLocationId?: number,
): WorkOrderServiceListResult {
    const fetch = useFetch();

    const params = useMemo<URLSearchParams | undefined>(() => {
        if (customerLocationId) {
            return new URLSearchParams({
                customerLocationId: customerLocationId.toString(),
            });
        }
    }, [customerLocationId]);

    const workOrdersResponse = usePaginationService<WorkOrderOverview, Error>(
        defaultPagination,
        WorkOrderListPath,
        params,
    );

    const editWorkOrder = useCallback<EditWorkOrderListCallback>(
        async (id, model) => {
            await fetch
                .put(WorkOrderEditPath(id), model)
                .finally(workOrdersResponse.reload);
        },
        [fetch, workOrdersResponse.reload],
    );

    const deleteWorkOrder = useCallback<DeleteWorkOrderListCallback>(
        async id => {
            await fetch
                .delete(WorkOrderDeletePath(id))
                .finally(workOrdersResponse.reload);
        },
        [fetch, workOrdersResponse.reload],
    );

    return {
        workOrdersResponse,
        editWorkOrder,
        deleteWorkOrder,
    };
}

export function useWorkOrderViewService(
    workOrderId: number,
    reload = true,
): WorkOrderServiceViewResult {
    const workOrderResponse = useSWR<WorkOrder | undefined, Error>(
        WorkOrderGetPath(workOrderId),
        {
            revalidateOnFocus: reload,
            revalidateOnReconnect: reload,
            refreshWhenOffline: reload,
            refreshWhenHidden: reload,
            refreshInterval: reload ? 0 : undefined,
        },
    );

    // const downloadWorkOrder = useCallback<DownloadWorkOrderListCallback>(
    //     async id => {
    //         setDownloading(true);
    //         try {
    //             const workOrder = await fetch
    //                 .get<WorkOrder>(WorkOrderGetPath(id))
    //                 .then(response => response.json());
    //             const blob = await fetch
    //                 .get(WorkOrderDownloadPath(id))
    //                 .then(response => response.blob());
    //             fileDownload(
    //                 blob,
    //                 `${formatCustomerName(workOrder)} - ${
    //                     workOrder.workListName
    //                 } - ${dateUtils.format(
    //                     fromUnixTime(workOrder.visitationDate),
    //                     "P",
    //                 )}.pdf`,
    //             );
    //         } finally {
    //             setDownloading(false);
    //         }
    //     },
    //     [dateUtils, fetch],
    // );

    return {
        workOrderResponse,
    };
}
